/**
 * PATH: packages/kanban-host/src/allocation/useKanbanLinkEmailToCard.ts
 * FILE_ID: KANBANHOST-ALLOC-LINK-CARD-001
 * PURPOSE: One-call hook that links an email onto an EXISTING Kanban
 *          card (the "link to existing card" arm of the allocate
 *          popover). Wraps `KanbanApi.linkEmailToCard` with the same
 *          FE side-effects `useKanbanQuickAllocate` applies: invalidate
 *          the email/kanban caches, bump recents, and surface a toast.
 *
 * WHY: Operator (2026-06-24): *"Allow me to allocate a Card which
 *      already exists in Kanban to the email ... with quick search."*
 *      `useKanbanQuickAllocate` always CREATES a card on a lane; this
 *      is its sibling for attaching to a card the operator picked.
 *
 * FLOW:
 *   run({ cardId, emailId })
 *     → KanbanApi.linkEmailToCard()
 *        ├─ ok    → toast + invalidate ['emails','kanban',…] + touchRecent
 *        └─ error → classify (perm / network / server) + toast
 *
 * RELATED:
 *   - ./useKanbanQuickAllocate : the create-a-card sibling; we reuse its
 *     INVALIDATION_KEYS + classifyAllocateError so the two paths stay
 *     consistent on cache invalidation and error copy.
 */

import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import type {
  AllocationSource,
  KanbanApi,
  LinkEmailToCardResult,
} from '@quotech/ui/kanban'

import { touchRecentBoard } from './boardPrefs'
import {
  buildInvalidationKeys,
  classifyAllocateError,
} from './useKanbanQuickAllocate'

export interface LinkEmailToCardRunInput {
  cardId: string
  emailId: string
  /** Optional board id — only used to bump the "recent boards" pref so
   *  the just-used board floats up next time the picker opens. */
  boardId?: string | null
  /** Defaults to USER_MANUAL. */
  source?: AllocationSource
}

export type LinkEmailToCardOutcome =
  | { kind: 'ok'; createdLink: boolean; result: LinkEmailToCardResult }
  | {
      kind: 'error'
      message: string
      status?: number | null
      retryable?: boolean
    }
  | { kind: 'busy'; message: string }

export interface LinkEmailToCardApi {
  busy: boolean
  error: string | null
  reset: () => void
  run: (input: LinkEmailToCardRunInput) => Promise<LinkEmailToCardOutcome>
}

export function useKanbanLinkEmailToCard(args: {
  api: KanbanApi
  orgId: number | null
  notify?: (kind: 'success' | 'error' | 'info', message: string) => void
  currentUserId?: number | string | null
}): LinkEmailToCardApi {
  const { api, orgId, notify, currentUserId = null } = args
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Synchronous in-flight guard — mirrors useKanbanQuickAllocate's
  // HARDEN-H5: two rapid clicks in the same tick both read busy===false
  // before either setState lands, so the ref is the authoritative gate.
  const inFlightRef = useRef(false)

  const reset = useCallback(() => {
    setError(null)
    setBusy(false)
    inFlightRef.current = false
  }, [])

  const run = useCallback<LinkEmailToCardApi['run']>(
    async (input) => {
      if (inFlightRef.current) {
        return { kind: 'busy', message: 'Link already in progress — please wait.' }
      }
      if (typeof api.linkEmailToCard !== 'function') {
        const message = 'Linking to an existing card is not available here.'
        setError(message)
        notify?.('error', message)
        return { kind: 'error', message, retryable: false }
      }
      inFlightRef.current = true
      setBusy(true)
      setError(null)
      try {
        const result = await api.linkEmailToCard({
          cardId: input.cardId,
          emailId: input.emailId,
          source: input.source ?? 'USER_MANUAL',
        })
        if (input.boardId) {
          touchRecentBoard(orgId, input.boardId, currentUserId)
        }
        for (const key of buildInvalidationKeys()) {
          void queryClient.invalidateQueries({ queryKey: [...key] })
        }
        const message = result.created_link
          ? 'Email linked to the card.'
          : 'Email was already linked to this card.'
        notify?.('success', message)
        return {
          kind: 'ok',
          createdLink: result.created_link,
          result,
        }
      } catch (err) {
        const classified = classifyAllocateError(err)
        setError(classified.message)
        notify?.('error', classified.message)
        return {
          kind: 'error',
          message: classified.message,
          status: classified.status,
          retryable: classified.retryable,
        }
      } finally {
        inFlightRef.current = false
        setBusy(false)
      }
    },
    [api, orgId, queryClient, notify, currentUserId],
  )

  return { busy, error, reset, run }
}
